import { useEffect, useState } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';

export function useTheme() {
  const [theme, setThemeState] = useState<ThemeMode>('system');
  const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>('light');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);

    // Get saved theme from localStorage
    const savedTheme = localStorage.getItem('theme') as ThemeMode;
    const initialTheme = savedTheme || 'system';
    if (savedTheme) {
      setThemeState(savedTheme);
    }

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const initialSystemTheme = mediaQuery.matches ? 'dark' : 'light';
    
    const handleChange = (e: MediaQueryListEvent) => {
      const newSystemTheme = e.matches ? 'dark' : 'light';
      setSystemTheme(newSystemTheme);
      
      // Apply theme to document if current theme is 'system'
      // Read current theme from localStorage to get the latest value
      const currentTheme = (localStorage.getItem('theme') as ThemeMode) || 'system';
      if (currentTheme === 'system') {
        const root = document.documentElement;
        root.classList.remove('light', 'dark');
        root.classList.add(newSystemTheme);
      }
    };

    setSystemTheme(initialSystemTheme);
    mediaQuery.addEventListener('change', handleChange);

    // Apply theme to document on initialization
    const root = document.documentElement;
    const currentTheme = initialTheme === 'system' ? initialSystemTheme : initialTheme;
    root.classList.remove('light', 'dark');
    root.classList.add(currentTheme);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const setTheme = (newTheme: ThemeMode) => {
    setThemeState(newTheme);
    localStorage.setItem('theme', newTheme);

    // Apply theme to document
    const root = document.documentElement;
    const currentTheme = newTheme === 'system' ? systemTheme : newTheme;

    root.classList.remove('light', 'dark');
    root.classList.add(currentTheme);
  };

  const currentTheme = theme === 'system' ? systemTheme : theme;

  return {
    theme,
    setTheme,
    systemTheme,
    mounted,
    resolvedTheme: currentTheme,
  };
}
